<template>
  <div id="page-business-list">
    <vx-card
      ref="filterCard"
      :title="$t('businesses.filters')"
      class="business-list-filters mb-8"
      @refresh="resetColFilters"
      refreshContentAction
      collapseAction
    >
      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full">
          <label class="text-sm opacity-75">{{ $t('forms.search') }}</label>
          <vs-input
            class="w-full"
            v-model="searchString"
            @input="updateSearchString"
            :placeholder="$t('forms.type_search')"
          />
        </div>
        <div class="vx-col md:w-1/4 w-full">
          <label class="text-sm opacity-75">{{ $t('entity.states.states') }}</label>
          <v-select
            :options="stateOptions"
            :clearable="false"
            taggable
            multiple
            :placeholder="$t('forms.select_option')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="stateFilter"
            class="w-full"
          />
        </div>
      </div>
    </vx-card>

    <vs-table
      ref="table"
      :data="businessesData"
      :total="paginationPageSize"
      @selected="handleSelected"
      :sst="true"
      @sort="handleSort"
      class="card-table"
    >
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
        <div>
          <!-- ITEMS PER PAGE -->
          <vs-dropdown vs-trigger-click class="cursor-pointer" v-if="businessesDataCount">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2">
                {{ (currentPage * paginationPageSize - (paginationPageSize - 1)) | persian_digits }}
                {{ $t('forms.to') }}
                {{
                  businessesDataCount - currentPage * paginationPageSize > 0
                    ? currentPage * paginationPageSize
                    : businessesDataCount | persian_digits
                }}
                {{ $t('forms.from') }} {{ businessesDataCount | persian_digits }}
              </span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="paginationSetPageSize(5)">
                <span>{{ 5 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(10)">
                <span>{{ 10 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(50)">
                <span>{{ 50 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(100)">
                <span>{{ 100 | persian_digits }}</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
          <!-- ADD NEW BTN -->
          <vs-button class="ml-4" @click="$router.push({ name: 'system-business-add' })">
            {{ $t('forms.add') }}
          </vs-button>
        </div>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>

      <template slot="thead">
        <vs-th>{{ $t('businesses.manager') }}</vs-th>
        <vs-th>{{ $t('places.places') }}</vs-th>
        <vs-th sort-key="plan">{{ $t('businesses.plans.plan') }}</vs-th>
        <vs-th sort-key="state">{{ $t('entity.state') }}</vs-th>
        <vs-th sort-key="expirationDate">{{ $t('businesses.expiration_date') }}</vs-th>
        <vs-th>{{ $t('forms.actions') }}</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody id="table-body" v-if="data.length">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.user.details.firstName }} {{ tr.user.details.lastName }}</vs-td>
            <vs-td>
              <ul>
                <li
                  v-for="place in tr.places"
                  :key="place._id"
                  :class="{
                    'text-success': place.state === 'approved' && place.status === 'active',
                    'text-danger': place.state === 'rejected',
                    'text-warning': place.state === 'pending'
                  }"
                >
                  {{ $t(`places.categories.${place.category}`) }}({{
                    $t(`places.subjects.${place.subject}`)
                  }}): {{ place.alias }}
                </li>
              </ul>
            </vs-td>
            <vs-td>
              <cell-badges :data="tr.plan" local="businesses.plans." />
            </vs-td>
            <vs-td>
              <cell-badges :data="tr.state" local="entity.states." />
            </vs-td>
            <vs-td>{{ tr.expirationDate | datetime }}</vs-td>
            <vs-td class="whitespace-no-wrap">
              <cell-actions :business="tr" />
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>
    <!-- PAGINATION -->
    <div>
      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
  </div>
</template>

<script>
// Store Module
import moduleBusiness from '@/store/business/moduleBusiness'

//Cells
import CellActions from './CellActions.vue'
import CellBadges from './CellBadges.vue'

export default {
  name: 'BusinessList',
  components: {
    CellActions,
    CellBadges
  },
  data() {
    return {
      // Filter Options
      stateFilter: [],

      query: {},

      searchString: '',

      //Sort
      sort: {},
      currentPage: 1
    }
  },
  watch: {
    stateFilter(obj) {
      this.setColumnFilter('state', obj)
    },
    currentPage(val) {
      this.$store.commit('business/SET_BUSINESSES_SKIP', (val - 1) * this.paginationPageSize)
      this.updateTable()
    }
  },
  computed: {
    stateOptions() {
      return this.$store.getters['enum/selectOptions']('State', 'entity.states')
    },
    businessesDataCount() {
      return this.$store.state.business.businessesCount
    },
    businessesData() {
      return this.$store.state.business.businesses
    },
    paginationPageSize() {
      return this.$store.state.business.limit
    },
    totalPages() {
      return this.$store.getters['business/totalPages']
    }
  },
  methods: {
    setQuery() {
      this.$store.commit('business/SET_BUSINESSES_QUERY', this.query)
      this.currentPage = 1
      this.updateTable()
    },
    updateTable() {
      this.$vs.loading({
        scale: 0.6
      })
      this.$store.dispatch('business/fetchBusinesses').then(() => {
        this.$vs.loading.close()
      })
      this.$store.dispatch('business/countBusinesses')
    },
    paginationSetPageSize(items) {
      this.$store.commit('business/SET_BUSINESSES_LIMIT', items)
      this.updateTable()
    },
    setColumnFilter(column, val) {
      if (val.length > 0) {
        const filter = []
        let i = null
        for (i in val) {
          filter.push(val[i].value ? val[i].value : val[i].label)
        }
        this.query[column] = { $in: filter }
      } else {
        delete this.query[column]
      }
      this.setQuery()
    },
    resetColFilters() {
      this.stateFilter = []
      this.searchString = ''
      this.query = {}
      this.setQuery()
      this.$refs.filterCard.removeRefreshAnimation()
    },
    updateSearchString() {
      if (this.searchString.length > 0) {
        const regex = { $regex: `/*${this.searchString}/*` }
        this.query['$or'] = [{ userId: regex }]
      } else {
        delete this.query['$or']
      }
      this.setQuery()
    },
    handleSort(col, active) {
      if (col === null) return
      if (active !== null) {
        this.sort[col] = active === 'asc' ? 1 : -1
        this.$store.commit('business/SET_BUSINESSES_SORT', this.sort)
      } else {
        this.$store.commit('business/SET_BUSINESSES_SORT', {})
      }
      this.setQuery()
    },
    handleSelected(business) {
      this.$router.push({ name: 'system-business-view', params: { id: business.id } })
    }
  },
  created() {
    if (!moduleBusiness.isRegistered) {
      this.$store.registerModule('business', moduleBusiness)
      moduleBusiness.isRegistered = true
    }
    this.$store.commit('business/SET_BUSINESSES_QUERY', this.query)

    this.updateTable()
  }
}
</script>
